import { P2PRateplan } from '@/containers/Accounts/types';

interface RateplanBreakdownItem {
  label: string;
  count: number;
}

interface RateplanCalculationInput {
  rateplan_breakdown?: RateplanBreakdownItem[];
  rateplan_adjusted?: number;
  leads_loaded: number;
  p2p_rateplan?: P2PRateplan;
}

interface RateplanCalculationResult {
  rateplan_savings_perc?: number;
  rateplan_savings_total_count?: number;
  rateplan_savings_total_count_perc?: number;
}

/**
 * Calculate rateplan savings metrics from the API response data
 */
export function calculateRateplanMetrics(
  input: RateplanCalculationInput
): RateplanCalculationResult {
  const { rateplan_breakdown, rateplan_adjusted, leads_loaded, p2p_rateplan } = input;

  // If no rateplan data, return empty
  if (
    !rateplan_breakdown ||
    !p2p_rateplan ||
    !rateplan_adjusted ||
    rateplan_adjusted === 0
  ) {
    return {};
  }

  const defaultLabel = p2p_rateplan.default_label;
  const standardPrice = p2p_rateplan.default_per_sms_success;

  // Calculate total count
  const totalCount = rateplan_breakdown.reduce((sum, item) => sum + item.count, 0);

  if (totalCount === 0) {
    return {};
  }

  // Calculate total standard cost (what it would cost at standard rate)
  const totalStandardCost = totalCount * standardPrice;

  // Calculate actual cost (sum of count × price for each tier)
  let actualCost = 0;
  for (const item of rateplan_breakdown) {
    if (item.label === defaultLabel) {
      // Standard tier uses default price
      actualCost += item.count * standardPrice;
    } else {
      // Find the rate for this tier
      const rate = p2p_rateplan.rates?.find((r) => r.label === item.label);
      if (rate) {
        actualCost += item.count * rate.per_sms_success;
      } else {
        // Fallback to standard price if rate not found
        actualCost += item.count * standardPrice;
      }
    }
  }

  // Calculate savings
  const savings = totalStandardCost - actualCost;

  // Calculate rateplan_savings_perc
  const rateplan_savings_perc =
    totalStandardCost > 0 ? (savings / totalStandardCost) * 100 : 0;

  // Calculate rateplan_savings_total_count (sum of all non-STANDARD tier contacts)
  const rateplan_savings_total_count = rateplan_breakdown
    .filter((item) => item.label !== defaultLabel)
    .reduce((sum, item) => sum + item.count, 0);

  // Calculate rateplan_savings_total_count_perc
  const rateplan_savings_total_count_perc =
    leads_loaded > 0 ? (rateplan_adjusted / leads_loaded) * 100 : 0;

  return {
    rateplan_savings_perc,
    rateplan_savings_total_count,
    rateplan_savings_total_count_perc,
  };
}
